import { useCallback, useEffect, useState } from 'react';
import { fetchEntityList, writeEntity } from '@/lib/entity-api';

/**
 * Cost codes in the frontend: the format rule (mirror of core/cost-codes.js and of the cost_codes_code_format check in the database --
 * a backend test keeps the three equal) and the company's codes as a hook for every select / combobox that picks one.
 * Records reference a cost code by its ID (cost_code_id); the text is only what the user reads, and can be edited at any time.
 */
export interface CostCode {
  id: string;
  code: string;
  name: string | null;
  description: string | null;
  /** null = a PARENT cost code (a group that only organises others); set = a CHILD cost code (what items, orders, bills and budgets use). Two levels. */
  parent_id?: string | null;
  /** '0000' on the company's one default CHILD (0100 · Default, under 0000 · Unassigned): the fallback of items without a cost code; it cannot be deleted. */
  default_key?: string | null;
  is_active: boolean;
  created_at: string;
}

/**
 * The company's DEFAULT cost code, from its own records (the child marked default_key): what every new line starts with. undefined when the company has
 * none -- no id is ever invented, and a line without a code cannot be saved.
 */
export const defaultCostCodeOf = (codes: CostCode[]): CostCode | undefined => codes.find((c) => !!c.default_key);

/** A child cost code is the only kind that can be chosen for an item, order, bill or budget line. */
export const isChildCode = (code: Pick<CostCode, 'parent_id'>): boolean => !!code.parent_id;

/**
 * The codes a select / combobox offers: the active CHILD codes, plus any code the record already has (e.g. one deactivated since, or a
 * legacy code that has no parent yet), so an existing document never loses its value.
 */
export const operationalCodes = (codes: CostCode[], keepIds: string[] = []): CostCode[] =>
  codes.filter((c) => (isChildCode(c) && c.is_active) || keepIds.includes(c.id));

export const MAX_COST_CODE_LENGTH = 32;
export const COST_CODE_PATTERN = /^[A-Za-z0-9]+(?:[ ._-][A-Za-z0-9]+)*$/;

/** Trimmed, whitespace collapsed to one space, no spaces around "." "_" "-", typographic dashes turned into "-". */
export const normalizeCostCode = (value: unknown): string =>
  String(value ?? '')
    .replace(/[‐-―−]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\s*([._-])\s*/g, '$1')
    .trim();

/** The key two spellings of one code share ("x-100" = "X-100"). */
export const costCodeKey = (value: unknown): string => normalizeCostCode(value).toLowerCase();

/** Why a (normalised) code is not valid, or null. */
export function costCodeProblem(code: string): string | null {
  if (!code) return 'Code is required';
  if (code.length > MAX_COST_CODE_LENGTH) return `Code must be at most ${MAX_COST_CODE_LENGTH} characters`;
  if (!COST_CODE_PATTERN.test(code)) return `Code may only contain letters and digits, separated by a single space, ".", "_" or "-" (e.g. 1000, 03 30 00, A-100)`;
  return null;
}

/** "1000 · Concrete", or just "1000" while the code has no name yet. */
export const costCodeLabel = (code: Pick<CostCode, 'code' | 'name'>): string => (code.name ? `${code.code} · ${code.name}` : code.code);

const byCode = (a: CostCode, b: CostCode) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });

export async function fetchCostCodeList(): Promise<CostCode[]> {
  return [...(await fetchEntityList<CostCode>('cost_code'))].sort(byCode);
}

/** Creates a cost code (the server validates and normalises again) and returns it. */
/** A code typed on the fly is a CHILD of `parentId` (callers pass the default 0000 · Unassigned parent until the user classifies it in Catalog). */
export async function createCostCode(text: string, parentId?: string | null): Promise<CostCode> {
  const code = normalizeCostCode(text);
  const problem = costCodeProblem(code);
  if (problem) throw new Error(problem);
  return writeEntity<CostCode>('api/entities/cost_code', 'POST', parentId ? { code, parent_id: parentId } : { code }, 'Failed to create the cost code');
}

/**
 * The company's cost codes for a select / combobox. `activeCodes` are the ones that can be newly chosen; `codes` also holds inactive ones
 * so a record that already uses one still shows its label. `add` puts a code created on the spot into the list.
 */
export function useCostCodes(enabled = true) {
  const [codes, setCodes] = useState<CostCode[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetchCostCodeList()
      .then((list) => { if (!cancelled) setCodes(list); })
      .catch((error) => console.error('Error loading the cost codes:', error))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled]);

  const add = useCallback((created: CostCode) => {
    setCodes((prev) => (prev.some((c) => c.id === created.id) ? prev : [...prev, created].sort(byCode)));
  }, []);

  return { codes, activeCodes: operationalCodes(codes), loading, add };
}
